import React from 'react';
import MoneyComponent from 'react/elements/MoneyComponent';
import CategoryColor from 'utilities/CategoryColor';
import LedgerCell from './LedgerCell';

const HEADINGS = {
	category: 'Category',
	budgeted: 'Budgetted',
	spent: 'Spent',
	remaining: 'Remain',
};

/**
 * The budget half of the ledger screen -- the spreadsheet's A3:D34, near enough cell for cell.
 *
 * The category cell is filled with the category's own colour rather than being coloured text, the
 * way her sheet fills it. `FILL_TEXT_COLOR` is a constant because the fill is held at one
 * luminance whatever the hue, so there is no black-or-white decision to make here.
 *
 * The fill goes on the name inside the cell rather than on the cell, which is what lets a
 * subcategory's band of colour start indented -- a family then reads as a staircase down the
 * column instead of as five rows that happen to be next to each other. See `familyClass()`.
 *
 * Deliberately not CategoryNameComponent: that draws the name in the category's *text* colour, for
 * the page background, and that colour over this fill is 3.3:1 -- under the 4.5 it was chosen to
 * clear. On a filled cell the fill carries the colour and the text stays black.
 *
 * @param pane {Object} from `budgetPane()` -- the same structure the cursor navigates
 * @param budgetCommit {Function} `(category, cents)`, called when a Budgetted cell is committed
 */
export default function LedgerBudgetBlock({ pane, paneIndex, budgetCommit }) {
	const rowsIn = (section) => pane.rows.filter((row) => row.section === section);

	const cellProps = (row, cellIndex) => ({
		key: row.cells[cellIndex].key,
		paneIndex,
		row,
		cellIndex,
	});

	const moneyEdit = (row, amount) => ({
		type: 'money',
		value: amount,
		commit: (cents) => budgetCommit(row.category, cents),
		clear: () => budgetCommit(row.category, 0),
	});

	function renderRow(row) {
		switch(row.kind) {
			case 'header':
				return (
					<tr key={row.key}>
						{row.cells.map((cell, cellIndex) => (
							<LedgerCell
								{...cellProps(row, cellIndex)}
								tag='th'
								className={cell.key === 'category' ? 'category' : 'amount'}
							>
								{HEADINGS[cell.key]}
							</LedgerCell>
						))}
					</tr>
				);
			case 'group':
				return (
					<tr key={row.key} className='group_heading'>
						<LedgerCell {...cellProps(row, 0)} tag='th'>{row.categoryGroup.name}</LedgerCell>
					</tr>
				);
			case 'category':
				return (
					<tr key={row.key} className={familyClass(row)}>
						<LedgerCell
							{...cellProps(row, 0)}
							className='category'
							title={row.category.fullName()}
						>
							<span
								className='category_name'
								style={{
									backgroundColor: row.category.getFillColor(),
									color: CategoryColor.FILL_TEXT_COLOR,
								}}
							>
								{row.category.name}
							</span>
						</LedgerCell>
						<LedgerCell {...cellProps(row, 1)} className='amount' edit={moneyEdit(row, row.budgeted)}>
							{row.usesParentBudget
								? <BudgetArrow row={row} />
								: <MoneyComponent amount={row.budgeted} />}
							{row.sharesBudgetWithChildren &&
							<span className='budget_arrow' title='Budgetted for the categories under it'>&#8595;</span>
							}
						</LedgerCell>
						<LedgerCell {...cellProps(row, 2)} className='amount'>
							<MoneyComponent amount={row.spent} />
						</LedgerCell>
						<LedgerCell {...cellProps(row, 3)} className='amount'>
							{row.usesParentBudget
								? <BudgetArrow row={row} />
								: <MoneyComponent amount={row.remaining} />}
						</LedgerCell>
					</tr>
				);
			case 'total':
				return (
					<tr key={row.key} className='total'>
						<LedgerCell {...cellProps(row, 0)} tag='th' className='category'>Total Monthly Budget</LedgerCell>
						<LedgerCell {...cellProps(row, 1)} className='amount'><MoneyComponent amount={row.totals.budgeted} /></LedgerCell>
						<LedgerCell {...cellProps(row, 2)} className='amount'><MoneyComponent amount={row.totals.spent} /></LedgerCell>
						<LedgerCell {...cellProps(row, 3)} className='amount'><MoneyComponent amount={row.totals.remaining} /></LedgerCell>
					</tr>
				);
			default:
				return null;
		}
	}

	return (
		<table className='ledger_budget_block'>
			<thead>{rowsIn('head').map(renderRow)}</thead>
			<tbody>{rowsIn('body').map(renderRow)}</tbody>
			<tfoot>{rowsIn('foot').map(renderRow)}</tfoot>
		</table>
	);
}

/**
 * What marks a row as part of a family, for the indent and the connector in `_ledger.scss`.
 *
 * `sortCategories()` puts a child directly under its parent, so the block already has the shape --
 * until this it just had no way of saying so, and a Food under Antoine Extra sat at the same
 * indent as a Food that belongs to nobody. Two of them can even be the same colour: the fill is
 * hashed from the name, so the Beauty under Antoine Extra and the Beauty under Jasmine Extra come
 * out identical. The indent is the only thing on this screen that tells them apart.
 */
function familyClass(row) {
	return [
		row.depth ? 'child_category' : '',
		row.hasChildren ? 'parent_category' : '',
	].filter(Boolean).join(' ');
}

/**
 * What stands in for a number a child of a master budget does not have one of.
 *
 * A zero would be a claim -- "budgeted nothing, and so overspent by everything it spent" -- about
 * money that is sitting one row up. The dashboard says this with the same arrow
 * (`CategorySummaryComponent`), pointing at the row that does hold the budget.
 */
function BudgetArrow({ row }) {
	const parent = row.category.getParent();
	return (
		<span
			className='budget_arrow'
			title={parent ? `Spends ${parent.name}'s budget` : 'Spends the parent category\'s budget'}
		>
			&#8593;
		</span>
	);
}
